import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useCurrentUser } from '../../../../../../hooks/useCurrentUser/useCurrentUser';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ProfileEntityFormProps, ProfileEntityFormSchema } from './ProfileEntityForm.types';
import { getProfileEntitySchema } from './ProfileEntityForm.schema';
import {
  CREATE_PENDING_ENTITY_QUERY_KEY,
  createPendingEntityQuery,
} from './ProfileEntityForm.query';
import { profileEntityData, profileEntityDisabledData } from './ProfileEntityForm.data';
import { usePlansENT } from '../../../../../../hooks/fetchers/usePlansENT/usePlansENT';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import Input from '../../../../../commons/Input/Input';
import Select from '../../../../../commons/Select/Select';
import { useAddressAutocomplete } from '../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete';
import { AddressSuggestion } from '../../../../../../types/models/address/address.types';
import departements from '../../../../../../assets/json/departements-region.json';
import Button from '../../../../../designSystem/Button/Button';
import { ButtonSize } from '../../../../../designSystem/Button/Button.types';
import Loading from '../../../../../commons/Loading/Loading';
import { useCurrentEntity } from '../../../../../../hooks/fetchers/useCurrentEntity/useCurrentEntity';
import { BaseResponse } from '../../../../../../types/api/api.types';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

export default ({ disabled, pendingEntity }: ProfileEntityFormProps) => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const currentUser = useCurrentUser();

  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const { data: addressSuggestions } = useAddressAutocomplete(searchInputValue);

  const { data: plansENT } = usePlansENT();

  // @ts-ignore
  const { data: entity, isInitialLoading } = useCurrentEntity();

  const formProps = useForm<ProfileEntityFormSchema>({
    resolver: yupResolver(getProfileEntitySchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid, errors },
  } = formProps;

  useEffect(() => {
    if (entity) {
      setValue('name', (pendingEntity || entity).name, { shouldValidate: true });
      setValue('address', (pendingEntity || entity).address, { shouldValidate: true });
      setValue('city', (pendingEntity || entity).city, { shouldValidate: true });
      setValue('postalCode', (pendingEntity || entity).postalCode, { shouldValidate: true });
      setValue('department', (pendingEntity || entity).department, { shouldValidate: true });
      setValue('region', (pendingEntity || entity).region, { shouldValidate: true });
      setValue('siret', (pendingEntity || entity).siret, { shouldValidate: true });
      setValue('firstName_owner', (pendingEntity || entity).firstName_owner, {
        shouldValidate: true,
      });
      setValue('lastName_owner', (pendingEntity || entity).lastName_owner, {
        shouldValidate: true,
      });
      setValue('email_owner', (pendingEntity || entity).email_owner, { shouldValidate: true });
      setValue('phone_owner', (pendingEntity || entity).phone_owner, { shouldValidate: true });
      setValue('function_owner', (pendingEntity || entity).function_owner, {
        shouldValidate: true,
      });
    }
  }, [entity]);

  const { mutate } = useMutation<Awaited<BaseResponse>, Error, ProfileEntityFormSchema>(
    (data) => createPendingEntityQuery(data),
    {
      mutationKey: CREATE_PENDING_ENTITY_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: () => {
        navigate(0);
      },
      onError: (error) => {
        alert(error.message);
      },
    }
  );

  const onAddressChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const value = e.target.value;
    onChange(e);
    setSearchInputValue(value);
  };

  const handleSelectedSuggestion = (suggestion: AddressSuggestion) => {
    setSearchInputValue('');
    setValue('address', suggestion.address);
    setValue('city', suggestion.city);
    setValue('postalCode', suggestion.postalCode);
    setValue('department', suggestion.department);
    setValue(
      'region',
      departements.find((res) => res.num_dep.toString() == suggestion.department_code)
        ?.region_name || ''
    );
    if (suggestion.coordinates) {
      setValue('latitude', suggestion.coordinates.latitude)
      setValue('longitude', suggestion.coordinates.longitude)
    }
  };

  const onSubmit = handleSubmit((data) => mutate(data));

  const updatedFormData = useMemo(
    () =>
      (disabled ? profileEntityDisabledData : profileEntityData).map((row) => {
        return row.map((item) => {
          switch (item.name) {
            default:
              return item;
          }
        });
      }),
    [plansENT, entity, currentUser]
  );

  if (isInitialLoading) return <Loading />;

  return (
    <form className="form">
      {updatedFormData.map((row, rowIndex) => (
        <div key={rowIndex} className="form__row">
          {row.map(
            ({ name, placeholder, label, type, options, defaultValue, ...props }, inputIndex) => (
              <Controller
                key={inputIndex}
                name={name}
                render={({ field: { onChange, value, ...rest } }) =>
                  name === 'address' ? (
                    <SearchInput
                      suggestions={addressSuggestions}
                      placeholder={placeholder || ''}
                      type={type}
                      onChange={(e) => onAddressChange(e, onChange)}
                      handleSelectedSuggestion={handleSelectedSuggestion}
                      error={errors[name]?.message as string}
                      // @ts-ignore
                      value={value}
                      {...{ label, name }}
                      {...props}
                    />
                  ) : type !== 'select' ? (
                    <Input
                      placeholder={placeholder || ''}
                      type={type}
                      disabled={Boolean(
                        ['department', 'region', 'city', 'postalCode'].includes(name)
                      )}
                      error={errors[name]?.message as string}
                      // @ts-ignore
                      value={value}
                      {...{ onChange, label, name }}
                      {...props}
                    />
                  ) : (
                    <Select
                      placeholder={placeholder || ''}
                      error={errors[name]?.message as string}
                      {...{ label, options, defaultValue, ...rest }}
                      {...props}
                      // @ts-ignore
                      onChange={({ value }: SelectOption) => {
                        onChange(value);
                      }}
                    />
                  )
                }
                {...{ control }}
              />
            )
          )}
        </div>
      ))}
      {/* <span style={{ marginBottom: 'var(--spacing-sToM)' }}>Pour toute modification des informations de votre entreprise, veuillez nous contacter à <a href='mailto:contact@publiconsult.fr'>contact@publiconsult.fr</a></span> */}
      {disabled && pendingEntity && (
        <span style={{ marginBottom: 'var(--spacing-sToM)' }}>
          Vos modifications effectuées le {moment(pendingEntity.created_at).format('DD/MM/YYYY')}{' '}
          sont en attente de validation.
        </span>
      )}
      <Button
        // style={{ display: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        size={ButtonSize.Large}
        disabled={!isValid || disabled}>
        Valider
      </Button>
    </form>
  );
};
